import { Icons } from '@cast/design-system';
import { lazyRetry } from '@cast/utils';

import { DboRoute } from 'features/db-optimization/v2/routes';
import { DboConfigSection, DboNavContext, DboNavItem } from './types';

const DboOverviewPage = lazyRetry(() => import('../DboOverviewPage'));
const DashboardPage = lazyRetry(() => import('../DboInstance/DashboardPage'));
const HealthPage = lazyRetry(() => import('../DboInstance/HealthPage'));
const ErrorsPage = lazyRetry(() => import('../DboInstance/ErrorsPage'));
const CachesPage = lazyRetry(() => import('../DboInstance/CachesPage'));
const ConnectionsPage = lazyRetry(
  () => import('../DboInstance/ConnectionsPage')
);
const PerformancePage = lazyRetry(
  () => import('../DboInstance/PerformancePage')
);
const AuditLogPage = lazyRetry(() => import('../DboInstance/AuditLogPage'));
const SettingsPage = lazyRetry(() => import('../DboInstance/SettingsPage'));
const SettingsEndpointsPage = lazyRetry(
  () => import('../DboInstance/SettingsEndpointsPage')
);
const DatabaseDashboardPage = lazyRetry(
  () => import('../DboInstance/DboDatabase/DashboardPage')
);
const DatabasePerformanceAdvisorPage = lazyRetry(
  () => import('../DboInstance/PerformanceAdvisor/PerformanceAdvisorPage')
);
const PerformanceAdvisorRecommendationPage = lazyRetry(
  () =>
    import(
      '../DboInstance/PerformanceAdvisor/PerformanceAdvisorRecommendationPage'
    )
);
const DatabaseConnectionsPage = lazyRetry(
  () => import('../DboInstance/DboDatabase/ConnectionsPage')
);
const DatabaseCachingOverviewPage = lazyRetry(
  () => import('../DboInstance/DboDatabase/Caching/OverviewPage')
);
const DatabaseCachingQueriesPage = lazyRetry(
  () => import('../DboInstance/DboDatabase/Caching/QueriesPage')
);
const QueryDetailsPage = lazyRetry(
  () => import('../DboInstance/DboDatabase/Caching/QueryDetailsPage')
);
const DatabaseCachingRulesPage = lazyRetry(
  () => import('../DboInstance/DboDatabase/Caching/RulesPage')
);

export const DBO_ROUTE_CONFIG: Record<DboConfigSection, DboNavItem[]> = {
  organization: [
    {
      route: DboRoute.OVERVIEW,
      title: 'Databases',
      breadcrumbs: [],
      page: DboOverviewPage,
    },
  ],
  server: [
    {
      route: DboRoute.SERVER_DASHBOARD,
      title: 'Dashboard',
      icon: Icons.Gauge,
      breadcrumbs: ['Dashboard'],
      page: DashboardPage,
    },
    {
      route: DboRoute.SERVER_HEALTH,
      title: 'Health',
      icon: Icons.Heartbeat,
      condition: (ctx: DboNavContext) => !ctx.isManualProvider,
      breadcrumbs: ['Health'],
      page: HealthPage,
    },
    {
      route: DboRoute.SERVER_ERRORS,
      title: 'Errors',
      icon: Icons.Warning,
      badge: 'new',
      condition: (ctx: DboNavContext) => ctx.isOptimized,
      // TEMPORARY (POLY-2202): hidden until the real upstream API ships —
      // gated by the `ui__standalone--dbo-query-errors` feature flag.
      rules: [['view', 'FeatureDboQueryErrors']],
      breadcrumbs: ['Errors'],
      page: ErrorsPage,
    },
    {
      route: DboRoute.SERVER_CACHES,
      title: 'Caches',
      icon: Icons.Cylinder,
      condition: (ctx: DboNavContext) => ctx.isOptimized,
      breadcrumbs: ['Caches'],
      page: CachesPage,
    },
    {
      route: DboRoute.SERVER_CONNECTIONS,
      title: 'Connections',
      icon: Icons.ArrowsLeftRight,
      condition: (ctx: DboNavContext) => ctx.isOptimized,
      breadcrumbs: ['Connections'],
      page: ConnectionsPage,
    },
    {
      route: DboRoute.SERVER_PERFORMANCE,
      title: 'Performance',
      icon: Icons.ChartLineUp,
      condition: (ctx: DboNavContext) => ctx.isOptimized,
      breadcrumbs: ['Performance'],
      page: PerformancePage,
    },
    {
      route: DboRoute.SERVER_AUDIT_LOG,
      title: 'Audit log',
      icon: Icons.FileText,
      condition: (ctx: DboNavContext) => ctx.isOptimized,
      breadcrumbs: ['Audit log'],
      page: AuditLogPage,
    },
    {
      route: DboRoute.SERVER_SETTINGS,
      title: 'Settings',
      icon: Icons.GearSix,
      breadcrumbs: ['Settings'],
      page: SettingsPage,
    },
    {
      route: DboRoute.SERVER_SETTINGS_ENDPOINTS,
      title: 'Endpoints',
      routeOnly: true,
      breadcrumbs: ['Settings', 'Endpoints'],
      page: SettingsEndpointsPage,
    },
  ],
  database: [
    {
      route: DboRoute.DB_DASHBOARD,
      title: 'Dashboard',
      breadcrumbs: ['Dashboard'],
      page: DatabaseDashboardPage,
    },
    {
      route: DboRoute.DB_PERFORMANCE_ADVISOR,
      title: 'Performance advisor',
      breadcrumbs: ['Performance advisor'],
      page: DatabasePerformanceAdvisorPage,
    },
    {
      route: DboRoute.DB_RECOMMENDATION,
      title: 'Recommendation',
      routeOnly: true,
      breadcrumbs: ['Performance advisor', 'Recommendation'],
      page: PerformanceAdvisorRecommendationPage,
    },
    {
      route: DboRoute.DB_CONNECTIONS,
      title: 'Connections',
      breadcrumbs: ['Connections'],
      page: DatabaseConnectionsPage,
    },
    {
      title: 'Caching',
      children: [
        {
          route: DboRoute.DB_CACHING_OVERVIEW,
          title: 'Overview',
          breadcrumbs: ['Caching', 'Overview'],
          page: DatabaseCachingOverviewPage,
        },
        {
          route: DboRoute.DB_CACHING_QUERIES,
          title: 'Queries',
          breadcrumbs: ['Caching', 'Queries'],
          page: DatabaseCachingQueriesPage,
        },
        {
          route: DboRoute.DB_CACHING_QUERY,
          title: 'Queries',
          routeOnly: true,
          breadcrumbs: ['Caching', 'Queries'],
          page: QueryDetailsPage,
        },
        {
          route: DboRoute.DB_CACHING_RULES,
          title: 'Rules',
          breadcrumbs: ['Caching', 'Rules'],
          page: DatabaseCachingRulesPage,
        },
      ],
    },
  ],
};
